import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from './providers/ConfigProvider';
import { AuthProvider } from './providers/AuthProvider';
import { UserSessionProvider } from './providers/UserSessionProvider';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { ChunkErrorBoundary } from './components/shared/ChunkErrorBoundary';
import { LoadingFallback } from './components/shared/LoadingFallback';
import { ToastProvider } from './components/shared/ToastContainer';
import { useAppStore } from './store/appStore';
import { MobileBottomNav } from './components/layout/MobileBottomNav';
import { SkipLink } from './components/shared/SkipLink';
import { CommandPalette } from './components/shared/CommandPalette';
import { KeyboardShortcutsHelp } from './components/shared/KeyboardShortcutsHelp';
import { GuidedTour } from './components/tour/GuidedTour';
import './components/shared/SkipLink.css';
import './components/shared/CommandPalette.css';
import './App.css';

const SearchPage = lazy(() => import('./pages/SearchPage').then(m => ({ default: m.SearchPage })));
const ObservationsPage = lazy(() => import('./pages/ObservationsPage').then(m => ({ default: m.ObservationsPage })));
const AvailabilityPage = lazy(() => import('./pages/AvailabilityPage').then(m => ({ default: m.AvailabilityPage })));
const StationQualityPage = lazy(() => import('./pages/StationQualityPage').then(m => ({ default: m.StationQualityPage })));
const ObservationsTabPage = lazy(() => import('./pages/ObservationsTabPage').then(m => ({ default: m.ObservationsTabPage })));
const StatisticsPage = lazy(() => import('./pages/StatisticsPage').then(m => ({ default: m.StatisticsPage })));
const NetworkPage = lazy(() => import('./pages/NetworkPage').then(m => ({ default: m.NetworkPage })));
const NetworkQualityPage = lazy(() => import('./pages/NetworkQualityPage').then(m => ({ default: m.NetworkQualityPage })));


// ponytail: 100% of users land on SearchPage — prefetch its chunk on idle so it's
// ready before React even renders the Suspense boundary. Eliminates the lazy-load waterfall.
if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
  requestIdleCallback(() => { import('./pages/SearchPage'); });
} else {
  setTimeout(() => { import('./pages/SearchPage'); }, 100);
}

function App() {
  // Pre-load all station data for autocomplete and map display.
  // Starts fetching immediately so data is ready by the time user navigates to search.
  // ponytail: This is the SINGLE source of station data — StationCacheService reads from
  // this store instead of making its own /api/weather/weatherstations call.
  useEffect(() => {
    const { loadAllStations } = useAppStore.getState();
    loadAllStations();
  }, []);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <ConfigProvider>
          <AuthProvider>
            <BrowserRouter>
              <UserSessionProvider>
                <SkipLink />
                <Routes>
                  <Route path="/" element={<Navigate to="/search/station" replace />} />
                  <Route path="/login" element={<Navigate to="/search/station" replace />} />
                  <Route path="/search/:method" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><SearchPage /></Suspense></ChunkErrorBoundary>} />
                  <Route path="/observations/:stationCode" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><ObservationsPage /></Suspense></ChunkErrorBoundary>} />
                  <Route path="/availability" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><AvailabilityPage /></Suspense></ChunkErrorBoundary>} />
                  <Route path="/statistics" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><StatisticsPage /></Suspense></ChunkErrorBoundary>} />
                  <Route path="/network" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><NetworkPage /></Suspense></ChunkErrorBoundary>} />
                  <Route path="/network/:networkId/quality" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><NetworkQualityPage /></Suspense></ChunkErrorBoundary>} />
                  <Route path="/station-quality" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><StationQualityPage /></Suspense></ChunkErrorBoundary>} />
                  <Route path="/observations" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><ObservationsTabPage /></Suspense></ChunkErrorBoundary>} />
                  <Route path="*" element={<Navigate to="/search/station" replace />} />
                </Routes>
                <MobileBottomNav />
                <CommandPalette />
                <KeyboardShortcutsHelp />
                <GuidedTour />
              </UserSessionProvider>
            </BrowserRouter>
          </AuthProvider>
        </ConfigProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
